import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { FormField, FormSheet, Input } from '@/shared/components';
import type { BookableResourceResponse } from '@/shared/api';
import { MinuteSelect } from './MinuteSelect';
import {
  CLOSING_OPTIONS,
  OPENING_OPTIONS,
  resourceDefaults,
  resourceSchema,
  type ResourceValues,
} from './resourceModel';
import { ResourceDescriptionFields } from './ResourceDescriptionFields';
import { ResourcePriceField } from './ResourcePriceField';
import { ResourceImagePanel } from './photos/ResourceImagePanel';
import { priceAcceptable } from './resourcePriceModel';
import { useResourceSave } from './useResources';

/** Anlegen und Bearbeiten einer Ressource; Deaktivieren ist Bearbeiten ohne Buchbarkeit. */
export function ResourceFormSheet({
  groupId,
  timezone,
  currency,
  minimum,
  resource,
  onClose,
}: {
  groupId: string;
  timezone: string;
  currency: string;
  minimum: number | null;
  resource?: BookableResourceResponse;
  onClose: () => void;
}) {
  const {
    register,
    control,
    watch,
    handleSubmit,
    formState: { errors, isDirty },
  } = useForm<ResourceValues>({
    resolver: zodResolver(resourceSchema),
    defaultValues: resourceDefaults(resource),
  });
  const { save, saving, error } = useResourceSave(groupId, resource, currency, onClose);
  const hourlyPrice = watch('hourlyPrice');

  return (
    <FormSheet
      title={resource ? `„${resource.name}“ bearbeiten` : 'Ressource anlegen'}
      description={`Exklusiv buchbar, im 30-Minuten-Raster. Die Zeiten gelten in der Zeitzone des Objekts (${timezone}).`}
      dirty={isDirty}
      saving={saving}
      // Eine Ressource mit ungültiger Währung ist auch ohne Änderung speicherbar: genau das verlangt ihr Hinweis.
      canSave={
        (resource ? isDirty || resource.priceCurrencyMismatch : true) &&
        priceAcceptable(hourlyPrice, currency, minimum)
      }
      error={error}
      submitLabel={resource ? 'Speichern' : 'Ressource anlegen'}
      onSubmit={handleSubmit((values) => save(values))}
      onClose={onClose}
    >
      <FormField label="Name" error={errors.name?.message} required>
        <Input {...register('name')} placeholder="z. B. Seminarraum" autoComplete="off" />
      </FormField>
      <div className="grid grid-cols-2 gap-4">
        <FormField label="Buchbar ab" error={errors.opensAtMinute?.message} required>
          <MinuteSelect control={control} name="opensAtMinute" options={OPENING_OPTIONS} />
        </FormField>
        <FormField label="Buchbar bis" error={errors.closesAtMinute?.message} required>
          <MinuteSelect control={control} name="closesAtMinute" options={CLOSING_OPTIONS} />
        </FormField>
      </div>
      <ResourcePriceField
        registration={register('hourlyPrice', { valueAsNumber: true })}
        hourlyPrice={hourlyPrice}
        currency={currency}
        minimum={minimum}
        resource={resource}
        error={errors.hourlyPrice?.message}
      />
      <ResourceDescriptionFields
        register={register}
        watch={watch}
        errors={{
          descriptionDe: errors.descriptionDe?.message,
          descriptionEn: errors.descriptionEn?.message,
        }}
      />
      <div>
        <label className="text-ink flex min-h-11 items-center gap-3 text-sm">
          <input
            type="checkbox"
            className="accent-sage size-5"
            aria-describedby="resource-active-hint"
            {...register('isActive')}
          />
          Für Gäste buchbar
        </label>
        <p id="resource-active-hint" className="text-ink-soft pl-8 text-[13px]">
          Deaktiviert verschwindet die Ressource aus jeder Auswahl; vergangene Reservierungen
          bleiben lesbar.
        </p>
      </div>
      <section aria-labelledby="resource-photos-title" className="space-y-2">
        <h3 id="resource-photos-title" className="text-ink text-sm font-semibold">
          Fotos für Gäste
        </h3>
        {resource ? (
          <ResourceImagePanel groupId={groupId} resourceId={resource.id} />
        ) : (
          <p className="text-ink-soft text-sm">Fotos lassen sich nach dem Anlegen hinzufügen.</p>
        )}
      </section>
    </FormSheet>
  );
}
